import { CheckOutlined, DatabaseOutlined, DeleteOutlined, EyeOutlined, HomeOutlined, PictureOutlined, SaveOutlined, ShareAltOutlined, UndoOutlined } from "@ant-design/icons";
import { faGear, faHeartPulse, faKey, faListCheck, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Badge, Button, Collapse, ConfigProvider, Divider, Form, Input, Menu, Space, Table, Tag, Tooltip, Typography, Upload } from "antd";
import { showError, showSuccess } from "common/ToastNotifications";
import Segment from "components/atoms/Segment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getContrastColor } from "theme/helpers";
import CypressTestIds from "../../../cypress/CypressTestIds";
import { DEFAULT_CUSTOMIZATION } from "../../../store/tenancyCustomization/constants";
import { selectCustomizationData } from "../../../store/tenancyCustomization/selectors";
import { buildAntTheme } from "../../../theme/buildAntTheme";
import { THEME_PRESETS } from "../../../theme/themePresets";
import HeaderNavigationBar from "../HeaderNavigationBar";
import DashboardIcon from "../HeaderNavigationBar/DashboardIcon";
import PageFooter from "../PageFooter";
import ColorRow from "./ColorRow";

const { Title, Text, Paragraph } = Typography;

export interface ThemeCustomizerProps {
    onSave: (data: ICustomizationData) => void;
    onResetToDefault?: () => void;
    isDataAvail?: boolean;
    showPreview?: boolean;
    className?: string;
    saveResult?: IResultState | null;
}

interface LogoState {
    file: File | null;
    previewUrl: string | null;
    width: number | null;
    height: number | null;
}

const FIELD_GROUPS: {
    key: string;
    label: string;
    fields: { key: keyof ICustomizationData; label: string }[];
}[] = [
    {
        key: "structure",
        label: "Structure & Navigation",
        fields: [
            { key: "colorNavigationBar", label: "Navigation Bar" },
            { key: "colorNavigationFont", label: "Navigation Font" },
            { key: "colorSelectedHeader", label: "Selected Header" },
            { key: "colorHeaderFooter", label: "Header & Footer" },
        ],
    },
    {
        key: "actions",
        label: "Buttons & Interactions",
        fields: [
            { key: "colorPrimaryButtons", label: "Primary Buttons" },
            { key: "colorSecondaryButtons", label: "Secondary Buttons" },
            { key: "colorSelectedRow", label: "Selected Row" },
            { key: "colorForm", label: "Form Background" },
        ],
    },
    {
        key: "typography",
        label: "Typography",
        fields: [
            { key: "colorMainHeadingFont", label: "Main Heading" },
            { key: "colorSubheadingFont", label: "Subheading" },
            { key: "colorParagraphFont", label: "Body Text" },
            { key: "colorLabel", label: "Form Labels" },
        ],
    },
];

const COLOR_TEST_IDS: Partial<Record<keyof ICustomizationData, string>> = {
    colorHeaderFooter: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_1,
    colorNavigationBar: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_2,
    colorSelectedHeader: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_3,
    colorPrimaryButtons: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_4,
    colorForm: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_5,
    colorSecondaryButtons: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_6,
    colorSelectedRow: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_7,
    colorLabel: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_8,
    colorParagraphFont: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_9,
    colorMainHeadingFont: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_10,
    colorSubheadingFont: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_11,
    colorNavigationFont: CypressTestIds.TENANCY_CUSTOMIZATION_COLOR_PALETTE_12,
};

function measureImage(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
            URL.revokeObjectURL(url);
        };
        img.onerror = reject;
        img.src = url;
    });
}

export function ThemeCustomizer({ onSave, onResetToDefault, isDataAvail = false, showPreview = true, className, saveResult }: ThemeCustomizerProps) {
    const saved = useSelector(selectCustomizationData) ?? DEFAULT_CUSTOMIZATION;

    const [draft, setDraft] = useState<ICustomizationData>(saved);
    const [logo, setLogo] = useState<LogoState>({
        file: null,
        previewUrl: saved.logo ?? null,
        width: saved.logoSize?.width ? Number(saved.logoSize.width) : null,
        height: saved.logoSize?.height ? Number(saved.logoSize.height) : null,
    });
    const [isDirty, setIsDirty] = useState(false);
    const [activeKeys, setActiveKeys] = useState<string[]>(["structure"]);

    const applyDraft = useCallback((next: ICustomizationData) => {
        setDraft(next);
        setIsDirty(true);
    }, []);

    const updateField = useCallback((key: keyof ICustomizationData, value: string) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
        setIsDirty(true);
    }, []);

    const handleLogoUpload = useCallback(async (file: File) => {
        try {
            const { width, height } = await measureImage(file);
            if (width !== height) {
                showError("Logo must be square (equal width and height).");
                return false;
            }
            if (width < 200) {
                showError("Logo must be at least 200 × 200 px.");
                return false;
            }
            if (width > 1024) {
                showError("Logo must not exceed 1024 × 1024 px.");
                return false;
            }
            const previewUrl = URL.createObjectURL(file);
            setLogo({ file, previewUrl, width, height });
            setIsDirty(true);
        } catch {
            showError("Could not read image dimensions");
        }
        return false;
    }, []);

    const handleLogoRemove = useCallback(() => {
        if (logo.previewUrl && logo.file) URL.revokeObjectURL(logo.previewUrl);
        setLogo({ file: null, previewUrl: null, width: null, height: null });
        setIsDirty(true);
    }, [logo]);

    const handleReset = useCallback(() => {
        setDraft(saved);
        setLogo({
            file: null,
            previewUrl: saved.logo ?? null,
            width: saved.logoSize?.width ? Number(saved.logoSize.width) : null,
            height: saved.logoSize?.height ? Number(saved.logoSize.height) : null,
        });
        setIsDirty(false);
    }, [saved]);

    // Re-sync draft when the store's saved data arrives or changes (e.g. after
    // the initial GET /api/v1/brand response, or after a post-save re-fetch).
    // Skipped when the user already has unsaved local changes (isDirty).
    useEffect(() => {
        if (isDirty) return;
        setDraft(saved);
        setLogo({
            file: null,
            previewUrl: saved.logo ?? null,
            width: saved.logoSize?.width ? Number(saved.logoSize.width) : null,
            height: saved.logoSize?.height ? Number(saved.logoSize.height) : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saved]);

    useEffect(() => {
        if (saveResult == null) return;
        if (saveResult.success === true) {
            setIsDirty(false);
            showSuccess("Theme saved");
        } else if (saveResult.success === false) {
            showError("Save failed. Please try again.");
        }
    }, [saveResult]);

    const handleSave = useCallback(() => {
        const payload: ICustomizationData = {
            ...draft,
            logo: logo.file ? (logo.file as unknown as string) : (logo.previewUrl ?? undefined),
            logoSize: logo.width && logo.height ? { width: logo.width, height: logo.height } : undefined,
        };
        onSave(payload);
    }, [draft, logo, onSave]);

    const previewTheme = useMemo(() => buildAntTheme(draft), [draft]);

    // ── Active preset lookup — used in two places below ───────────────────────
    const activePreset = useMemo(
        () => THEME_PRESETS.find((p) => p.customization.colorPrimaryButtons === draft.colorPrimaryButtons && p.customization.colorHeaderFooter === draft.colorHeaderFooter),
        [draft],
    );

    return (
        <>
            <div className={`flex h-full min-h-0 w-full overflow-hidden ${className ?? ""}`}>
                {/* ══════════════════════════════════════════════════════════
                    SIDE PANEL — inherits parent ConfigProvider, no overrides
                ══════════════════════════════════════════════════════════ */}
                <div className="flex w-80 flex-shrink-0 flex-col overflow-hidden border-r border-gray-200">
                    {/* ── Panel header ── */}
                    <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
                        <div>
                            <Text strong data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_TITLE_TEXT}>
                                Tenancy Customization
                            </Text>
                            <br />
                            <Text type="secondary" className="text-xs">
                                Changes preview instantly
                            </Text>
                        </div>
                        {isDirty && <Tag color="warning">Unsaved</Tag>}
                    </div>

                    {/* ── Scrollable body ── */}
                    <div className="scroll-webkit flex-1 overflow-y-auto">
                        {/* ── Logo ── */}
                        <div className="px-4 py-4">
                            <Text strong className="mb-2 block text-xs uppercase tracking-widest">
                                Logo
                            </Text>

                            {logo.previewUrl ? (
                                <div className="bg-layout relative flex min-h-20 items-center justify-center overflow-hidden rounded-lg border border-gray-200 p-3">
                                    <img
                                        src={logo.previewUrl}
                                        alt="Logo preview"
                                        className="max-h-20 max-w-full object-contain"
                                        data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_IMAGE_PREVIEW}
                                    />
                                    <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleLogoRemove}
                                        className="absolute right-2 top-2"
                                        data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_DELETE_IMAGE_BUTTON}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={handleLogoUpload}
                                    className="block w-full *:w-full"
                                    data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_IMAGE_INPUT}
                                >
                                    <div className="group flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 p-5 transition-colors hover:bg-secondary">
                                        <PictureOutlined className="mb-1.5 text-2xl group-hover:text-secondary" />
                                        <span className="block text-xs group-hover:text-secondary">Click to upload logo</span>
                                        <span className="text-xs opacity-60 group-hover:text-secondary">PNG, JPG</span>
                                        <span className="text-xs opacity-60 group-hover:text-secondary">(200x200 - 1024x1024 pixels)</span>
                                    </div>
                                </Upload>
                            )}

                            {logo.width && logo.height && (
                                <div className="mt-3 flex flex-col gap-1">
                                    <Form.Item
                                        label={
                                            <Text type="secondary" className="text-xs">
                                                Width (px)
                                            </Text>
                                        }
                                        className="mb-0 flex-1"
                                    >
                                        <Input size="small" value={logo.width} onChange={(e) => setLogo((l) => ({ ...l, width: Number(e.target.value) }))} type="number" />
                                    </Form.Item>
                                    <Form.Item
                                        label={
                                            <Text type="secondary" className="text-xs">
                                                Height (px)
                                            </Text>
                                        }
                                        className="mb-0 flex-1"
                                    >
                                        <Input size="small" value={logo.height} onChange={(e) => setLogo((l) => ({ ...l, height: Number(e.target.value) }))} type="number" />
                                    </Form.Item>
                                </div>
                            )}
                        </div>

                        <Divider className="my-0" />

                        {/* ── Identity ── */}
                        <div className="px-4 py-4">
                            <Text strong className="mb-3 block text-xs uppercase tracking-widest">
                                Identity
                            </Text>
                            <Form layout="vertical" size="small">
                                <Form.Item label="Company Name" className="mb-3">
                                    <Input
                                        value={draft.companyName ?? ""}
                                        onChange={(e) => updateField("companyName" as keyof ICustomizationData, e.target.value)}
                                        placeholder="ABC Corp"
                                        data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_TEXT_INPUT_1}
                                    />
                                </Form.Item>
                                <Form.Item label="Footer Slogan" className="mb-0">
                                    <Input
                                        value={draft.sloganFooter}
                                        onChange={(e) => updateField("sloganFooter" as keyof ICustomizationData, e.target.value)}
                                        placeholder="Intelligent choice for good customer data"
                                        data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_TEXT_INPUT_2}
                                    />
                                </Form.Item>
                            </Form>
                        </div>

                        <Divider className="my-0" />

                        {/* ── Colour presets ── */}
                        <div className="px-4 py-4">
                            <Text strong className="mb-0.5 block text-xs uppercase tracking-widest">
                                Colour Presets
                            </Text>
                            <Text type="secondary" className="mb-3 block text-xs">
                                10 WCAG AA–compliant palettes. Click to preview instantly.
                            </Text>
                            <div className="grid grid-cols-5 gap-2">
                                {THEME_PRESETS.map((preset, index) => {
                                    const isActive =
                                        draft.colorPrimaryButtons === preset.customization.colorPrimaryButtons &&
                                        draft.colorHeaderFooter === preset.customization.colorHeaderFooter;
                                    return (
                                        <Tooltip
                                            key={preset.key}
                                            title={
                                                <>
                                                    <Text strong className="block text-xs text-white">
                                                        {preset.label}
                                                    </Text>
                                                    <Text className="text-xs text-white opacity-80">{preset.description}</Text>
                                                </>
                                            }
                                            placement={index < 5 ? "top" : "bottom"}
                                        >
                                            <button
                                                onClick={() => {
                                                    const { sloganFooter: _sf, companyName: _cn, logo: _logo, logoSize: _ls, version: _v, ...colorFields } = preset.customization;
                                                    applyDraft({ ...draft, ...colorFields });
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.12)")}
                                                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                                className={[
                                                    "relative aspect-square w-full cursor-pointer rounded-md p-0 transition-transform",
                                                    "shadow-sm",
                                                    isActive ? "ring-2 ring-primary ring-offset-1" : "ring-0",
                                                ].join(" ")}
                                                style={{ background: preset.swatch, border: "none" }}
                                            >
                                                {isActive && <CheckOutlined className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-white" />}
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </div>

                            {activePreset && (
                                <div className="mt-2 flex items-center gap-2">
                                    <div className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: activePreset.swatch }} />
                                    <Text type="secondary" className="text-xs">
                                        <Text strong className="text-xs">
                                            {activePreset.label}
                                        </Text>
                                        {" — "}
                                        {activePreset.description}
                                    </Text>
                                </div>
                            )}

                            <Text type="secondary" className="mt-2 block text-xs">
                                For more color options, you may want to use online tools such as{" "}
                                <a href="https://www.w3schools.com/colors/colors_picker.asp" target="_blank" rel="noopener noreferrer" className="text-secondary">
                                    W3Schools Color Picker
                                </a>
                                .
                            </Text>
                        </div>

                        <Divider className="my-0" />

                        {/* ── Colour field groups ── */}
                        <Collapse
                            activeKey={activeKeys}
                            onChange={(keys) => setActiveKeys(keys as string[])}
                            ghost
                            className="rounded-none"
                            items={FIELD_GROUPS.map((group) => ({
                                key: group.key,
                                label: (
                                    <Text strong className="text-xs uppercase tracking-widest">
                                        {group.label}
                                    </Text>
                                ),
                                children: (
                                    <>
                                        {group.fields.map((f) => (
                                            <ColorRow
                                                key={f.key}
                                                fieldKey={f.key}
                                                label={f.label}
                                                value={(draft[f.key] as string) ?? "#000000"}
                                                onUpdate={updateField}
                                                testId={COLOR_TEST_IDS[f.key]}
                                            />
                                        ))}
                                    </>
                                ),
                            }))}
                        />
                    </div>

                    {/* ── Footer actions ── */}
                    <div className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-200 px-4 py-3">
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saveResult?.inProgress === true}
                            disabled={!isDirty}
                            onClick={handleSave}
                            block
                            data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_SUBMIT_BUTTON}
                        >
                            Save to Database
                        </Button>
                        <Button
                            icon={<UndoOutlined />}
                            disabled={!isDirty || saveResult?.inProgress === true}
                            onClick={handleReset}
                            block
                            data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_CANCEL_BUTTON}
                        >
                            Discard Changes
                        </Button>
                        {onResetToDefault && (
                            <Button
                                danger
                                disabled={!isDataAvail || saveResult?.inProgress === true}
                                onClick={onResetToDefault}
                                block
                                data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_RESET_BUTTON}
                            >
                                Reset to Default
                            </Button>
                        )}
                    </div>
                </div>

                {/* ══════════════════════════════════════════════════════════
                    PREVIEW PANEL — uses draft ConfigProvider
                ══════════════════════════════════════════════════════════ */}
                {showPreview && (
                    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                        <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 px-4 py-2">
                            <EyeOutlined className="text-secondary" />
                            <Text type="secondary" className="text-xs">
                                Live Preview — changes apply instantly
                            </Text>
                            {isDirty && (
                                <Tag color="warning" className="ml-auto">
                                    Previewing unsaved changes
                                </Tag>
                            )}
                        </div>

                        <ConfigProvider theme={previewTheme}>
                            <div className="flex flex-1 flex-col overflow-hidden">
                                <HeaderNavigationBar
                                    companyName={draft.companyName ?? "Pretectum Preview"}
                                    logo={logo.previewUrl ?? undefined}
                                    logoDimensions={logo.previewUrl ? { width: 120, height: 30 } : undefined}
                                    colors={draft}
                                    leftItems={[
                                        { title: "Home", icon: <HomeOutlined />, active: true },
                                        { title: "Dashboard", icon: <DashboardIcon /> },
                                        { title: "Schema", icon: <DatabaseOutlined /> },
                                        { title: "Relationships", icon: <ShareAltOutlined /> },
                                    ]}
                                    rightItems={[
                                        { title: "Tasks", icon: <FontAwesomeIcon icon={faListCheck} />, hideTitle: true },
                                        { title: "Configurations", icon: <FontAwesomeIcon icon={faGear} />, hideTitle: true },
                                        { title: "Jobs and Events", icon: <FontAwesomeIcon icon={faHeartPulse} />, hideTitle: true },
                                        { title: "License Info", icon: <FontAwesomeIcon icon={faKey} />, hideTitle: true },
                                        { title: "User Profile", icon: <FontAwesomeIcon icon={faUser} />, hideTitle: true },
                                    ]}
                                    testId={CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_NAVIGATION_BAR}
                                />
                                <div className="flex flex-1 overflow-hidden">
                                    <div className="w-[200px] p-1">
                                        <Menu
                                            selectedKeys={["2"]}
                                            items={[
                                                { key: "1", label: "Menu Item 1" },
                                                { key: "2", label: "Menu Item 2" },
                                                { key: "3", label: "Menu Item 3" },
                                            ]}
                                        />
                                    </div>
                                    <main className="scroll-webkit flex-1 space-y-4 overflow-y-auto bg-gray-50 p-5">
                                        <div>
                                            <Title level={3} style={{ color: draft.colorMainHeadingFont, margin: 0 }}>
                                                Records Management
                                            </Title>
                                            <Title level={5} style={{ color: draft.colorSubheadingFont, marginTop: 4 }}>
                                                Schema Overview
                                            </Title>
                                            <Paragraph style={{ color: draft.colorParagraphFont, fontSize: 13 }}>Manage and track all your records across the platform.</Paragraph>
                                        </div>

                                        <div className="px-1 pb-2">
                                            <Segment shadow>
                                                <Text
                                                    className="mb-3 block text-xs font-semibold uppercase tracking-widest"
                                                    style={{ color: draft.colorLabel }}
                                                    data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_LABEL_COLOR}
                                                >
                                                    Actions
                                                </Text>
                                                <Space wrap>
                                                    <Button type="primary" data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_PRIMARY_BUTTONS}>
                                                        Primary
                                                    </Button>
                                                    <Button>Default</Button>
                                                    <Button
                                                        style={{ background: draft.colorSecondaryButtons, color: getContrastColor(draft.colorSecondaryButtons) }}
                                                        data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_SECONDARY_BUTTONS}
                                                    >
                                                        Secondary
                                                    </Button>
                                                    <Button type="dashed">Dashed</Button>
                                                    <Button type="text">Text</Button>
                                                    <Button type="link">Link</Button>
                                                    <Button danger>Danger</Button>
                                                    <Tag color={draft.colorPrimaryButtons}>Active</Tag>
                                                    <Badge count={4} style={{ background: draft.colorSecondaryButtons }}>
                                                        <div className="h-8 w-8 rounded border border-gray-200 bg-gray-100" />
                                                    </Badge>
                                                </Space>
                                            </Segment>
                                        </div>

                                        <div className="px-1 pb-2">
                                            <Segment shadow>
                                                <Text className="mb-3 block text-xs font-semibold uppercase tracking-widest" style={{ color: draft.colorLabel }}>
                                                    Form Fields
                                                </Text>
                                                <Form layout="vertical">
                                                    <Form.Item label={<span style={{ color: draft.colorLabel }}>Client Name</span>}>
                                                        <Input
                                                            placeholder="ABC Corp"
                                                            style={{ background: draft.colorForm }}
                                                            data-testid={CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_FORM_BACKGROUND}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label={<span style={{ color: draft.colorLabel }}>Reference</span>}>
                                                        <Input placeholder="REF-001" style={{ background: draft.colorForm }} />
                                                    </Form.Item>
                                                </Form>
                                            </Segment>
                                        </div>

                                        <div className="px-1 pb-2">
                                            <Segment shadow>
                                                <Text className="mb-3 block text-xs font-semibold uppercase tracking-widest" style={{ color: draft.colorLabel }}>
                                                    Data Table
                                                </Text>
                                                <Table
                                                    size="small"
                                                    rowSelection={{ type: "checkbox" }}
                                                    columns={[
                                                        { title: "ID", dataIndex: "id" },
                                                        { title: "Client", dataIndex: "client" },
                                                        { title: "Status", dataIndex: "status" },
                                                    ]}
                                                    dataSource={[
                                                        { key: 1, id: "REC-001", client: "ABC Corp", status: <Tag color="success">Active</Tag> },
                                                        { key: 2, id: "REC-002", client: "Global Ltd", status: <Tag color="warning">Pending</Tag> },
                                                        { key: 3, id: "REC-003", client: "Initial Corp", status: <Tag color="default">Draft</Tag> },
                                                        { key: 4, id: "REC-004", client: "Albion Inc", status: <Tag color="error">Error</Tag> },
                                                    ]}
                                                    rowClassName={() => ""}
                                                    onRow={(_, index) => ({
                                                        style: index === 0 ? { background: draft.colorSelectedRow } : {},
                                                        ...(index === 0 ? { "data-testid": CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_SELECTED_ROW } : {}),
                                                    })}
                                                    pagination={false}
                                                />
                                            </Segment>
                                        </div>

                                        <Space orientation="vertical" className="w-full">
                                            <Alert title="Record saved successfully." type="success" showIcon />
                                            <Alert title="Pending approval." type="warning" showIcon />
                                            <Alert title="Error processing record." type="error" showIcon />
                                        </Space>
                                    </main>
                                </div>

                                <PageFooter
                                    sloganFooter={draft.sloganFooter}
                                    colors={{
                                        colorHeaderFooter: draft.colorHeaderFooter,
                                        colorNavigationFont: draft.colorNavigationFont,
                                    }}
                                    testId={CypressTestIds.TENANCY_CUSTOMIZATION_PREVIEW_HEADER_FOOTER}
                                />
                            </div>
                        </ConfigProvider>
                    </div>
                )}
            </div>
        </>
    );
}
